import React, { useState } from "react";
import { TextField, Button, MenuItem, Box, Typography, Alert, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { app, auth } from "../firebase/firebaseConfig";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";
import { logActivity } from "../utils/activityLogger";

import { Helmet } from "react-helmet-async";

const roleOptions = ["Admin", "Manager", "Member"];

export default function Signup() {
  const [companyName, setCompanyName] = useState("");
  const [licenseKey, setLicenseKey] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const functions = getFunctions(app, "us-central1");

  const handleSignup = async () => {
  setError("");

  if (!companyName || !licenseKey || !name || !email || !role || !password || !confirmPassword) {
    setError("All fields (including License Key) are required.");
    return;
  }
  if (password !== confirmPassword) { setError("Passwords do not match."); return; }
  if (password.length < 6) { setError("Password should be at least 6 characters."); return; }

  setLoading(true);
  try {
   
    // 1) Create auth user (this signs the browser in)
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const user = cred.user;

    // 2) Ensure the Functions SDK will include THIS user's ID token
    await user.getIdToken(true);
    await new Promise((resolve) => {
      const unsub = auth.onAuthStateChanged((u) => {
        if (u && u.uid === user.uid) { unsub(); resolve(); }
      });
    });

    let companyId = "";

    const whoAmI = httpsCallable(functions, "whoAmI");
      console.log("whoAmI:", (await whoAmI()).data);


    if (role === "Admin") {
      const claim = httpsCallable(functions, "claimLicense");
      const res = await claim({
        companyName,
        licenseKey: licenseKey.trim(),
      });
      companyId = res.data.companyId;
    } else {
      const validate = httpsCallable(functions, "validateCompanyLicense");
      const res = await validate({
        companyName,
        licenseKey: licenseKey.trim(),
      });
      companyId = res.data.companyId;
    }

    // 3) Create the user profile
    await setDoc(doc(db, "users", user.uid), {
      uid: user.uid,
      name,
      email,
      role,
      companyId,
      companyName,
      createdAt: serverTimestamp(),
      licenseVerifiedAt: serverTimestamp(),
    });

    // 4) Activity log, then go to dashboard
    await logActivity({
      userId: user.uid,
      userName: name,
      companyId,
      action: "SIGNUP",
      module: "User",
      details: `New ${role} registered with email ${email} for ${companyName}`,
    });

    navigate("/dashboard");
  } catch (e) {
    console.error(e);
    setError(e?.message || "Signup failed. Please check the license and try again.");
  } finally {
    
    setLoading(false);
  }
};


  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(180deg, rgba(25,118,210,0.08) 0%, rgba(25,118,210,0.02) 100%)" }}>

<Helmet>
  <title>Signup with your License Key | Start Free Trial - email Try Demo | WorkPulse.in</title>

  <meta
    name="description"
    content="Create your WorkPulse workspace and start managing entities, projects, tasks, timesheets and approvals. Get started in minutes."
  />

  <link rel="canonical" href="https://workpulse.in/signup" />

  {/* OpenGraph */}
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="WorkPulse.in" />
  <meta property="og:url" content="https://workpulse.in/" />
  <meta property="og:title" content="Never lose track of client work" />
  <meta
    property="og:description"
    content="Entity-wise workflow, project management, timesheets and activity trail in one secure workspace. Built for Chartered Accountants in India."
  />
  <meta property="og:image" content="https://workpulse.in/og-workpulse.png" />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />

  {/* Twitter (works even without a Twitter account) */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Never lose track of client work" />
  <meta
    name="twitter:description"
    content="Entity-wise workflow, project management, timesheets and activity trail in one secure workspace."
  />
  <meta name="twitter:image" content="https://workpulse.in/og-workpulse.png" />
</Helmet>




      <Box sx={{ width: "100%", maxWidth: 450, p: 4, backgroundColor: "white", borderRadius: 3, boxShadow: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>Create Your Account</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField label="Company Name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} fullWidth sx={{ mb: 2 }} />
        <TextField label="License Key" value={licenseKey} onChange={(e) => setLicenseKey(e.target.value)} placeholder="e.g., WP-ABCD-1234" fullWidth sx={{ mb: 2 }} />
        <TextField label="Full Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth sx={{ mb: 2 }} />
        <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth sx={{ mb: 2 }} />
        <TextField select label="Role" value={role} onChange={(e) => setRole(e.target.value)} fullWidth sx={{ mb: 2 }}>
          {roleOptions.map((r) => (<MenuItem key={r} value={r}>{r}</MenuItem>))}
        </TextField>
        <TextField label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} fullWidth sx={{ mb: 2 }} />
        <TextField label="Confirm Password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} fullWidth sx={{ mb: 3 }} />

        <Button variant="contained" color="primary" fullWidth onClick={handleSignup} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : "Sign Up"}
        </Button>

        <Typography variant="body2" align="center" sx={{ mt: 2 }}>
          Already have an account? <a href="/login" style={{ color: "#1976d2" }}>Login here</a>
        </Typography>
      </Box>
    </Box>
  );
}
